import React, { useState, useEffect, useMemo } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { Helmet, HelmetProvider } from "react-helmet-async";
import { useDispatch } from "react-redux";
import config from "../config";
import noimage from "../image/noimage.jpg";
import { addToCart } from "../redux/slice/CartSlice";
import { trackViewContent, trackAddToCart } from "../analytics/events";

export default function ProductDetail() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { slug, category } = useParams();

  const [product,         setProduct]         = useState(null);
  const [gallery,         setGallery]         = useState([]);
  const [selectedImage,   setSelectedImage]   = useState(noimage);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [variantGroups,   setVariantGroups]   = useState([]);
  const [relatedProduct,  setRelatedProduct]  = useState([]);
  const [loading,         setLoading]         = useState(true);
  const [addedToCart,     setAddedToCart]     = useState(false);
  const [qty,             setQty]             = useState(1);
  const [activeTab,       setActiveTab]       = useState("description");

  useEffect(() => {
    setLoading(true);
    window.scrollTo(0, 0);
    axios.get(`${config.apiUrl}/product/${slug}`)
      .then((res) => {
        const p = res.data.data;
        setProduct(p);
        if (p.gallery?.length) {
          setGallery(p.gallery);
          setSelectedImage(p.gallery[0]);
        } else if (p.thumbnail) {
          setGallery([p.thumbnail]);
          setSelectedImage(p.thumbnail);
        }
        if (p.variants?.length) {
          const groups = p.variants.reduce((acc, v) => {
            if (!acc[v.label]) acc[v.label] = [];
            acc[v.label].push(v);
            return acc;
          }, {});
          setVariantGroups(Object.entries(groups));
          const first = p.variants.find((v) => v.stock_qty > 0) || p.variants[0];
          setSelectedVariant(first);
          if (first?.image) setSelectedImage(first.image);
        }
        setLoading(false);
        trackViewContent(p);
      })
      .catch(() => navigate("/error"));
  }, [slug, navigate]);

  useEffect(() => {
    if (!product) return;
    const catSlug = product.category?.slug || category;
    axios.get(`${config.apiUrl}/product?category=${catSlug}&limit=5`)
      .then((res) => {
        setRelatedProduct(
          (res.data.data || []).filter((r) => r.slug !== slug).slice(0, 4)
        );
      })
      .catch(() => {});
  }, [product, slug, category]);

  const displayPrice = Number(selectedVariant?.discount_price || product?.discount_price) || 0;
  const displayMRP   = Number(selectedVariant?.price          || product?.price)           || 0;
  const discount     = displayMRP > displayPrice
    ? Math.round(((displayMRP - displayPrice) / displayMRP) * 100)
    : 0;
  const savings      = displayMRP > displayPrice ? displayMRP - displayPrice : 0;

  const handleVariantSelect = (variant) => {
    setSelectedVariant(variant);
    if (variant.image) setSelectedImage(variant.image);
    setAddedToCart(false);
  };

  const handleAddToCart = () => {
    if (!product) return;
    dispatch(addToCart({ product, variant: selectedVariant, quantity: qty }));
    trackAddToCart(product, selectedVariant, qty);
    setAddedToCart(true);
    setTimeout(() => setAddedToCart(false), 2500);
  };

  const handleBuyNow = () => {
    if (!product) return;
    dispatch(addToCart({ product, variant: selectedVariant, quantity: qty }));
    trackAddToCart(product, selectedVariant, qty);
    navigate("/checkout");
  };

  const jsonLd = useMemo(() => {
    if (!product) return null;
    return {
      "@context": "https://schema.org",
      "@type": "Product",
      name:        product.title,
      image:       gallery,
      description: product.meta_description || product.title,
      sku:         String(product.id),
      brand:       { "@type": "Brand", name: "MeraGym" },
      offers: {
        "@type":       "Offer",
        url:           `https://meragym.com/shop/${category}/${slug}`,
        priceCurrency: "INR",
        price:         displayPrice,
        availability:  "https://schema.org/InStock",
        seller:        { "@type": "Organization", name: "MeraGym" },
      },
    };
  }, [product, gallery, displayPrice, category, slug]);

  /* ── Loading skeleton ─────────────────────────────────────── */
  if (loading) return (
    <div className="pd-page">
      <div className="container">
        <div className="pd-skeleton-grid">
          <div className="mg-skeleton" style={{ height: 480, borderRadius: 16 }}></div>
          <div>
            <div className="mg-skeleton mg-skeleton-line" style={{ width: "70%", marginBottom: 16 }}></div>
            <div className="mg-skeleton mg-skeleton-line" style={{ width: "40%", marginBottom: 12 }}></div>
            <div className="mg-skeleton mg-skeleton-line" style={{ width: "30%", marginBottom: 32 }}></div>
            <div className="mg-skeleton mg-skeleton-line" style={{ width: "100%", marginBottom: 10 }}></div>
            <div className="mg-skeleton mg-skeleton-line" style={{ width: "100%", marginBottom: 10 }}></div>
            <div className="mg-skeleton mg-skeleton-line" style={{ width: "80%" }}></div>
          </div>
        </div>
      </div>
    </div>
  );

  if (!product) return null;

  const tabs = [
    { key: "description", label: "Description" },
    { key: "details",     label: "Product Details" },
    { key: "shipping",    label: "Shipping & Returns" },
  ];

  return (
    <>
      <HelmetProvider>
        <Helmet>
          <title>{product.meta_title || product.title} | MeraGym Shop</title>
          <meta name="description" content={product.meta_description || `Buy ${product.title} online at MeraGym. Best price, fast shipping across India.`} />
          <meta name="keywords" content={product.meta_keyword || product.title} />
          <link rel="canonical" href={`https://meragym.com/shop/${category}/${slug}`} />
          <meta property="og:title"       content={`${product.title} | MeraGym Shop`} />
          <meta property="og:description" content={product.meta_description || product.title} />
          <meta property="og:image"       content={product.thumbnail} />
          <meta property="og:url"         content={`https://meragym.com/shop/${category}/${slug}`} />
          <meta property="og:type"        content="product" />
          <meta name="twitter:card"       content="summary_large_image" />
          <meta name="twitter:title"      content={`${product.title} | MeraGym Shop`} />
          <meta name="twitter:image"      content={product.thumbnail} />
          {jsonLd && <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>}
        </Helmet>
      </HelmetProvider>

      <div className="pd-page">
        <div className="container">

          {/* Breadcrumb */}
          <nav className="pd-breadcrumb" aria-label="breadcrumb">
            <Link to="/shop"><i className="bi bi-shop me-1"></i>Shop</Link>
            <i className="bi bi-chevron-right"></i>
            <Link to={`/shop/${product.category?.slug}`}>{product.category?.title}</Link>
            <i className="bi bi-chevron-right"></i>
            <span>{product.title}</span>
          </nav>

          {/* Main Grid */}
          <div className="pd-main-grid">

            {/* ── Gallery ── */}
            <div className="pd-gallery">
              <div className="pd-thumbs">
                {gallery.map((img, i) => (
                  <button
                    key={i}
                    className={`pd-thumb${selectedImage === img ? " active" : ""}`}
                    onClick={() => setSelectedImage(img)}
                    aria-label={`View image ${i + 1}`}
                  >
                    <img src={img} alt={`${product.title} ${i + 1}`} onError={(e) => { e.target.src = noimage; }} />
                  </button>
                ))}
              </div>
              <div className="pd-main-img-wrap">
                {discount > 0 && (
                  <span className="pd-img-badge">-{discount}%</span>
                )}
                <img
                  src={selectedImage}
                  alt={product.title}
                  className="pd-main-img"
                  onError={(e) => { e.target.src = noimage; }}
                />
              </div>
            </div>

            {/* ── Info Panel ── */}
            <div className="pd-info-panel">

              {/* Store link */}
              <Link to="/shop" className="pd-store-link">
                <i className="bi bi-shop me-1"></i>MeraGym Official Store
              </Link>

              {/* Category */}
              <span className="pd-category-tag">{product.category?.title}</span>

              {/* Title */}
              <h1 className="pd-title">{product.title}</h1>

              {/* Price */}
              <div className="pd-price-block">
                <div className="pd-price-row">
                  <span className="pd-sale-price">₹{displayPrice.toLocaleString("en-IN")}</span>
                  {discount > 0 && (
                    <span className="pd-discount-pill">-{discount}% OFF</span>
                  )}
                </div>
                {displayMRP > displayPrice && (
                  <div className="pd-mrp-row">
                    <span className="pd-mrp-label">M.R.P:</span>
                    <span className="pd-mrp-value">₹{displayMRP.toLocaleString("en-IN")}</span>
                    <span className="pd-savings">You save ₹{savings.toLocaleString("en-IN")}</span>
                  </div>
                )}
                <p className="pd-tax-note">
                  <i className="bi bi-check-circle-fill"></i> Inclusive of all taxes
                </p>
              </div>

              {/* Variants */}
              {variantGroups.map(([label, variants]) => (
                <div className="pd-variant-group" key={label}>
                  <p className="pd-variant-label">
                    {label}: <strong>{selectedVariant?.label === label ? selectedVariant.value : variants.find(v => v.id === selectedVariant?.id)?.value || ""}</strong>
                  </p>
                  <div className="pd-variant-options">
                    {variants.map((v) => (
                      <button
                        key={v.id}
                        className={`pd-variant-btn${selectedVariant?.id === v.id ? " selected" : ""}${v.stock_qty === 0 ? " oos" : ""}`}
                        onClick={() => handleVariantSelect(v)}
                        title={v.stock_qty === 0 ? "Out of stock" : v.value}
                        disabled={v.stock_qty === 0}
                      >
                        {v.value}
                        {v.stock_qty === 0 && <span className="pd-oos-line"></span>}
                      </button>
                    ))}
                  </div>
                </div>
              ))}

              {/* Quantity */}
              <div className="pd-qty-row">
                <span className="pd-qty-label">Quantity</span>
                <div className="pd-qty-stepper">
                  <button onClick={() => setQty((q) => Math.max(1, q - 1))} aria-label="Decrease">−</button>
                  <span>{qty}</span>
                  <button onClick={() => setQty((q) => Math.min(20, q + 1))} aria-label="Increase">+</button>
                </div>
              </div>

              {/* CTAs */}
              <div className="pd-cta-row">
                <button
                  className={`pd-add-btn${addedToCart ? " added" : ""}`}
                  onClick={handleAddToCart}
                >
                  {addedToCart
                    ? <><i className="bi bi-check-circle-fill me-2"></i>Added to Cart!</>
                    : <><i className="bi bi-cart-plus me-2"></i>Add to Cart</>}
                </button>
                <button className="pd-buy-btn" onClick={handleBuyNow}>
                  <i className="bi bi-lightning-fill me-2"></i>Buy Now
                </button>
              </div>

              {/* Trust Strip */}
              <div className="pd-trust-strip">
                <div className="pd-trust-item">
                  <i className="bi bi-truck"></i>
                  <div>
                    <strong>Free Delivery</strong>
                    <span>Orders above ₹999</span>
                  </div>
                </div>
                <div className="pd-trust-item">
                  <i className="bi bi-arrow-repeat"></i>
                  <div>
                    <strong>Easy Returns</strong>
                    <span>7-day policy</span>
                  </div>
                </div>
                <div className="pd-trust-item">
                  <i className="bi bi-shield-check"></i>
                  <div>
                    <strong>Secure Payment</strong>
                    <span>100% safe</span>
                  </div>
                </div>
              </div>

              {/* Quick specs table */}
              <div className="pd-specs-box">
                <div className="pd-spec-row">
                  <span>Category</span>
                  <span>{product.category?.title}</span>
                </div>
                <div className="pd-spec-row">
                  <span>Brand</span>
                  <span>MeraGym</span>
                </div>
                <div className="pd-spec-row">
                  <span>Country of Origin</span>
                  <span>India</span>
                </div>
                <div className="pd-spec-row">
                  <span>Availability</span>
                  <span className="pd-instock"><i className="bi bi-check-circle-fill me-1"></i>In Stock</span>
                </div>
              </div>
            </div>
          </div>

          {/* ── Tabbed Section ── */}
          <div className="pd-tabs-section">
            <div className="pd-tabs-nav">
              {tabs.map((t) => (
                <button
                  key={t.key}
                  className={`pd-tab-btn${activeTab === t.key ? " active" : ""}`}
                  onClick={() => setActiveTab(t.key)}
                >
                  {t.label}
                </button>
              ))}
            </div>

            <div className="pd-tab-body">
              {activeTab === "description" && (
                <div
                  className="pd-desc-content"
                  dangerouslySetInnerHTML={{ __html: product.description || "<p>No description available.</p>" }}
                />
              )}

              {activeTab === "details" && (
                <div className="pd-details-tab">
                  <table className="pd-details-table">
                    <tbody>
                      <tr><td>Product Name</td><td>{product.title}</td></tr>
                      <tr><td>Category</td><td>{product.category?.title}</td></tr>
                      <tr><td>Brand</td><td>MeraGym</td></tr>
                      <tr><td>Country of Origin</td><td>India</td></tr>
                      {selectedVariant && <tr><td>Selected Variant</td><td>{selectedVariant.label}: {selectedVariant.value}</td></tr>}
                      <tr><td>SKU</td><td>MG-{product.id}</td></tr>
                    </tbody>
                  </table>
                </div>
              )}

              {activeTab === "shipping" && (
                <div className="pd-shipping-tab">
                  <div className="pd-shipping-grid">
                    <div className="pd-shipping-card">
                      <i className="bi bi-truck"></i>
                      <h4>Free Delivery</h4>
                      <p>Free shipping on all orders above ₹999. Standard delivery in 2–5 business days across India.</p>
                    </div>
                    <div className="pd-shipping-card">
                      <i className="bi bi-arrow-repeat"></i>
                      <h4>Easy Returns</h4>
                      <p>Not satisfied? Return within 7 days of delivery. Items must be unused and in original packaging.</p>
                    </div>
                    <div className="pd-shipping-card">
                      <i className="bi bi-headset"></i>
                      <h4>Need Help?</h4>
                      <p>Contact our support team at <a href="mailto:support@meragym.com">support@meragym.com</a> or call <a href="tel:+916390008506">+91 63900 08506</a>.</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* ── Related Products ── */}
          {relatedProduct.length > 0 && (
            <div className="pd-related-section">
              <div className="shop-section-head">
                <div>
                  <h2>Related Products</h2>
                  <p>You might also like these</p>
                </div>
                <Link to={`/shop/${product.category?.slug}`} className="shop-see-all">
                  View All <i className="bi bi-arrow-right-circle-fill ms-1"></i>
                </Link>
              </div>
              <div className="pd-related-grid">
                {relatedProduct.map((r) => {
                  const rDisc = Number(r.price) > Number(r.discount_price)
                    ? Math.round(((r.price - r.discount_price) / r.price) * 100) : 0;
                  return (
                    <Link key={r.id} to={`/shop/${r.category?.slug}/${r.slug}`} className="mg-pcard">
                      <div className="mg-pcard-img-wrap">
                        {rDisc > 0 && <span className="mg-pcard-badge-disc">-{rDisc}%</span>}
                        <img src={r.thumbnail || noimage} alt={r.title} onError={(e) => { e.target.src = noimage; }} className="mg-pcard-img" />
                      </div>
                      <div className="mg-pcard-body">
                        <span className="mg-pcard-cat">{r.category?.title}</span>
                        <h3 className="mg-pcard-title">{r.title}</h3>
                        <div className="mg-pcard-price-row">
                          <span className="mg-pcard-sale">₹{Number(r.discount_price).toLocaleString("en-IN")}</span>
                          {Number(r.price) > Number(r.discount_price) && (
                            <span className="mg-pcard-mrp">₹{Number(r.price).toLocaleString("en-IN")}</span>
                          )}
                        </div>
                      </div>
                    </Link>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
